import { Link } from "@tanstack/react-router"
import { ArrowRight, AudioLines } from "lucide-react"
import { AnimatePresence, motion } from "motion/react"

import { STAGE_SPRING } from "@/components/coach-stage"
import { PageContainer } from "@/components/page-container"
import type { Recommendation } from "@/components/recommendation-tiles"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { greetingFor } from "@/lib/greeting"
import { cn } from "@/lib/utils"
import { initialsOf } from "@nl/shared"

import {
  ComposerSlot,
  ProgressPanel,
  RecentList,
  moduleRows,
  tileImage,
  type StartLayoutProps,
} from "./shared"

/** The caption grey of neueleben.de, darker than the CI grey in the tokens. */
const WEB_GREY = "#696969"

/** The small corner bracket neueleben.de puts on its boxes and tiles. */
function CornerMark({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 20 20"
      aria-hidden
      className={cn("h-5 w-5", className)}
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
    >
      <path d="M4 3h13v13" />
    </svg>
  )
}

function CdNlTile({ item }: { item: Recommendation }) {
  const body = (
    <>
      <div className="relative aspect-[16/10] overflow-hidden bg-muted">
        <img
          src={tileImage(item.imageUrl, "wide", "menschen")}
          alt=""
          loading="lazy"
          className={cn(
            "absolute inset-0 h-full w-full object-cover dark:brightness-90",
            "motion-safe:transition-transform motion-safe:duration-700 motion-safe:ease-out motion-safe:group-hover:scale-[1.04]",
            !item.available && "saturate-[.6]"
          )}
        />
      </div>
      <div className="relative flex min-h-[92px] flex-1 items-end p-5 pr-7 text-white"
        style={{ backgroundColor: item.available ? WEB_GREY : "#9a9a9a" }}
      >
        <CornerMark className="absolute top-4 right-4 text-white" />
        <div className="min-w-0">
          <div className="text-[10.5px] tracking-[0.04em] uppercase text-white/75">
            {item.available ? item.kind : "Bald verfügbar"}
          </div>
          <div className="mt-1 text-[14.5px] leading-snug">{item.title}</div>
        </div>
      </div>
    </>
  )
  const frame = cn(
    "group flex h-full flex-col overflow-hidden outline-none",
    item.available
      ? "transition-shadow hover:shadow-[0_12px_32px_rgba(31,31,31,0.18)] focus-visible:ring-2 focus-visible:ring-ring/60"
      : "cursor-default"
  )
  if (!item.available || !item.to) {
    return (
      <div className={frame} aria-disabled>
        {body}
      </div>
    )
  }
  return (
    <Link to={item.to} search={item.search} className={frame}>
      {body}
    </Link>
  )
}

/**
 * Draft "CD NL": the language of neueleben.de. Straight edges, a solid red
 * box on the hero photo, grey caption blocks under the tiles with the corner
 * mark, red headings in regular weight, outlined white buttons with an arrow.
 * Pictures show people in everyday situations, as the website does, not the
 * city.
 */
export function CdNlLayout(p: StartLayoutProps) {
  const [first, ...rest] = moduleRows(p.recommendations)
  return (
    <>
      <section className="relative w-full overflow-hidden bg-[var(--surface-alt)]">
        <img
          src="/fotos/hero-hafen.jpg"
          alt=""
          aria-hidden
          className="absolute inset-0 h-full w-full object-cover dark:opacity-70"
        />
        <div className="relative z-10 mx-auto flex min-h-[560px] w-full max-w-[1100px] items-center justify-end px-8 py-14">
          <motion.div
            layoutId="coach-card"
            transition={STAGE_SPRING}
            className="relative w-full max-w-[560px] bg-[var(--nl-red)] p-8 text-white shadow-[0_24px_60px_rgba(0,0,0,0.25)] sm:p-10"
          >
            <AnimatePresence mode="wait" initial={false}>
              {p.voice ? (
                <motion.div
                  key="voice"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="-m-8 overflow-hidden bg-[var(--paper)] text-foreground sm:-m-10"
                >
                  {p.voice}
                </motion.div>
              ) : (
                <motion.div
                  key="prompt"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  <CornerMark className="absolute top-6 right-6 h-6 w-6 text-white" />
                  <div className="flex items-center gap-3.5">
                    <Avatar className="size-14 shrink-0 ring-2 ring-white/70">
                      {p.coach?.portraitUrl ? (
                        <AvatarImage src={p.coach.portraitUrl} alt="" />
                      ) : null}
                      <AvatarFallback className="bg-white/15 text-[16px] text-white">
                        {p.coach ? initialsOf(p.coach.name) : ""}
                      </AvatarFallback>
                    </Avatar>
                    <div className="leading-tight">
                      <div className="text-[15px]">{p.coachName}</div>
                      <div className="text-[13px] text-white/80">Dein KI-Coach</div>
                    </div>
                  </div>
                  <h1 className="mt-6 text-[30px] leading-[1.18] sm:text-[34px]">
                    {greetingFor(new Date())}
                    {p.firstName ? `, ${p.firstName}` : ""}.
                    <br />
                    Woran arbeiten wir heute?
                  </h1>
                  {p.standHint ? (
                    <p className="mt-3 text-[14px] text-white/85">{p.standHint}</p>
                  ) : null}
                  <div className="mt-6 flex flex-col gap-2.5 sm:flex-row sm:items-stretch">
                    <ComposerSlot>
                      <div className="bg-[var(--paper)] text-foreground">{p.composer}</div>
                    </ComposerSlot>
                    <button
                      type="button"
                      onClick={p.onSpeak}
                      className="flex min-h-11 shrink-0 items-center gap-2 border border-white px-4 text-[14px] text-white transition-colors hover:bg-white hover:text-[var(--nl-red)]"
                    >
                      <AudioLines className="h-4 w-4" />
                      Sprechen
                      <ArrowRight className="h-4 w-4" />
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </div>
      </section>

      <PageContainer className="mt-12 pb-14">
        {/* The first heading gets its own grid row, so the side panel starts
            exactly level with the first row of tiles, whatever the heading's
            height. */}
        <div className="grid gap-x-10 lg:grid-cols-[minmax(0,1fr)_300px] lg:grid-rows-[auto_1fr]">
          <h2 className="mb-5 text-[26px] leading-tight text-primary lg:col-start-1 lg:row-start-1">
            {first.title}
          </h2>
          <div className="min-w-0 space-y-12 lg:col-start-1 lg:row-start-2">
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {first.items.map((item) => (
                <CdNlTile key={item.key} item={item} />
              ))}
            </div>
            {rest.map((row) => (
              <section key={row.title}>
                <h2 className="mb-5 text-[26px] leading-tight text-primary">{row.title}</h2>
                <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                  {row.items.map((item) => (
                    <CdNlTile key={item.key} item={item} />
                  ))}
                </div>
              </section>
            ))}
          </div>
          <aside className="mt-10 space-y-10 lg:col-start-2 lg:row-start-2 lg:mt-0">
            <ProgressPanel
              resume={p.resume}
              completedCount={p.completedCount}
              accent="red"
              flat
            />
            <RecentList
              entries={p.entries}
              isLoading={p.entriesLoading}
              coachName={p.coachName}
              flat
            />
          </aside>
        </div>
      </PageContainer>
    </>
  )
}
